import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, CustomInput, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import {
  OPEN_SETTINGS_EVENT,
  getConsent,
  initAnalytics,
  setConsent,
  trackPageView
} from "../services/analytics";

/** Cookie banner + preferences dialog; analytics starts only after "accept". */
export default function CookieConsent() {
  const { t } = useTranslation();
  const location = useLocation();
  const [decided, setDecided] = useState(() => getConsent() != null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analyticsChoice, setAnalyticsChoice] = useState(() => getConsent()?.analytics === true);

  useEffect(() => {
    initAnalytics();
    const open = () => {
      setAnalyticsChoice(getConsent()?.analytics === true);
      setSettingsOpen(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, open);
  }, []);

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  const decide = async (allowAnalytics) => {
    await setConsent({ analytics: allowAnalytics });
    setDecided(true);
    setSettingsOpen(false);
    if (allowAnalytics) trackPageView(location.pathname);
  };

  return (
    <>
      {!decided && !settingsOpen &&
      <div className="fd-cookie-banner shadow-lg" role="dialog" aria-live="polite" data-testid="cookie-banner"
      style={{ position: "fixed", insetInline: 16, bottom: 16, zIndex: 1060, background: "#fff", borderRadius: 12, padding: "16px 20px", maxWidth: 720, marginInline: "auto" }}>
          <p className="mb-2 small">
            {t("cookies.bannerText")}{" "}
            <a href="/auth/privacy" target="_blank" rel="noopener noreferrer">{t("cookies.policyLink")}</a>
          </p>
          <div className="d-flex flex-wrap justify-content-end" style={{ gap: 8 }}>
            <Button size="sm" color="link" onClick={() => setSettingsOpen(true)}>{t("cookies.customize")}</Button>
            <Button size="sm" color="secondary" outline onClick={() => decide(false)}>{t("cookies.necessaryOnly")}</Button>
            <Button size="sm" color="primary" onClick={() => decide(true)}>{t("cookies.acceptAll")}</Button>
          </div>
        </div>
      }

      <Modal isOpen={settingsOpen} toggle={() => setSettingsOpen(false)} centered data-testid="cookie-settings">
        <ModalHeader toggle={() => setSettingsOpen(false)}>{t("cookies.settingsTitle")}</ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <CustomInput type="switch" id="cookie-necessary" label={t("cookies.necessaryTitle")} checked disabled readOnly />
            <small className="text-muted d-block mt-1">{t("cookies.necessaryDesc")}</small>
          </div>
          <div>
            <CustomInput type="switch" id="cookie-analytics" label={t("cookies.analyticsTitle")}
              checked={analyticsChoice} onChange={(e) => setAnalyticsChoice(e.target.checked)} />
            <small className="text-muted d-block mt-1">{t("cookies.analyticsDesc")}</small>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => decide(false)}>{t("cookies.necessaryOnly")}</Button>
          <Button color="primary" onClick={() => decide(analyticsChoice)}>{t("cookies.save")}</Button>
        </ModalFooter>
      </Modal>
    </>);

}
